/**
 * A `One of` roadmap item ([#6077](https://github.com/CodeinPlace/cip/issues/6077))
 * stands for several Math IDE assignments of which the student does one. It
 * is one node on the roadmap; a click offers the choices, and the node is
 * complete once any one of them is. Pure rules over the item, shared by the
 * student views, the completion map, the portfolio and the editor.
 */

/**
 * The assignment ids a `One of` item offers: its `choices`, with blanks and
 * repeats dropped, in the authored order.
 *
 * @param item - The roadmap item, or any object with a `choices` field.
 * @return The ids; empty when the field is missing or not a list of strings.
 * @throws Never.
 */
export function getOneOfChoices(item: {choices?: unknown}): string[] {
  if (!Array.isArray(item.choices)) return [];
  const ids: string[] = [];
  for (const choice of item.choices) {
    if (typeof choice !== 'string') continue;
    const id = choice.trim();
    if (id === '' || ids.includes(id)) continue;
    ids.push(id);
  }
  return ids;
}

/**
 * Whether a `One of` item is complete: true once any choice is, false when
 * every choice is known to be incomplete.
 *
 * @param args.choices - The item's choices (see {@link getOneOfChoices}).
 * @param args.isAssnCompleted - The completion lookup, which returns
 *   `undefined` while the student's progress is still loading.
 * @return `true`, `false`, or `undefined` while any choice is still
 *   loading and none is complete yet.
 * @throws Never.
 */
export function isOneOfComplete({
  choices,
  isAssnCompleted,
}: {
  choices: readonly string[];
  isAssnCompleted: (assignmentId: string) => boolean | undefined;
}): boolean | undefined {
  const states = choices.map(isAssnCompleted);
  if (states.some(state => state === true)) return true;
  return states.includes(undefined) ? undefined : false;
}

/**
 * The Math IDE path of one assignment.
 *
 * @param args.courseId - The course.
 * @param args.assignmentId - The assignment.
 * @return The in-app path.
 * @throws Never.
 */
export function getMathAssignmentPath({
  courseId,
  assignmentId,
}: {
  courseId: string;
  assignmentId: string;
}): string {
  return `/${courseId}/mide/a/${assignmentId}`;
}
